import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGUAGES } from "../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full text-sm bg-white border border-[#E7DFD5] rounded-xl px-4 py-3 outline-none focus:border-[#C85A17] transition-colors text-[#17181A]";

export const EnrollmentForm = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", track: "", status: "", language: lang });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/enroll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSuccess(true);
      toast.success(t.form.successTitle);
    } catch {
      toast.error(t.chat.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enroll" className="py-20 sm:py-28 bg-[#FAF7F2]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}
          className="text-xs font-mono uppercase tracking-[0.22em] text-[#C85A17] font-semibold text-center">
          {t.form.overline}
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }}
          className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#17181A] mt-4 text-center" data-testid="enrollment-heading">
          {t.form.heading}
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
          className="text-sm sm:text-base text-[#5F6368] text-center mt-4 mb-10 max-w-xl mx-auto">
          {t.form.desc}
        </motion.p>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
              className="rounded-2xl border border-[#163832]/20 bg-[#E9F2EF] p-10 text-center" data-testid="enrollment-success-banner">
              <CheckCircle2 className="w-12 h-12 text-[#163832] mx-auto mb-4" />
              <h3 className="font-display text-2xl font-semibold text-[#163832]">{t.form.successTitle}</h3>
              <p className="text-[#5F6368] mt-2">{t.form.successMsg}</p>
            </motion.div>
          ) : (
            <motion.form key="form" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} exit={{ opacity: 0 }}
              onSubmit={submit} className="rounded-2xl border border-[#E7DFD5] bg-white p-7 sm:p-10 space-y-5 shadow-[0_20px_50px_-30px_rgba(23,24,26,0.25)]"
              data-testid="enrollment-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-[#5F6368] mb-2">{t.form.nameLabel}</label>
                  <input data-testid="enrollment-form-name-input" required value={form.name} onChange={set("name")} placeholder={t.form.namePlaceholder} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-[#5F6368] mb-2">{t.form.emailLabel}</label>
                  <input data-testid="enrollment-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={t.form.emailPlaceholder} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-[#5F6368] mb-2">{t.form.trackLabel}</label>
                <select data-testid="enrollment-form-track-select" required value={form.track} onChange={set("track")} className={inputCls}>
                  <option value="" disabled>{t.form.selectPlaceholder}</option>
                  {t.programs.items.map((p) => (
                    <option key={p.id} value={p.title}>{p.title}</option>
                  ))}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-[#5F6368] mb-2">{t.form.statusLabel}</label>
                  <select data-testid="enrollment-form-status-select" required value={form.status} onChange={set("status")} className={inputCls}>
                    <option value="" disabled>{t.form.selectPlaceholder}</option>
                    {t.form.statuses.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-[#5F6368] mb-2">{t.form.langLabel}</label>
                  <select data-testid="enrollment-form-language-select" required value={form.language} onChange={set("language")} className={inputCls}>
                    {LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button data-testid="enrollment-form-submit-button" type="submit" disabled={submitting}
                className="w-full bg-[#C85A17] text-[#FDFCF7] font-semibold rounded-full px-6 py-4 hover:bg-[#a84a12] transition-colors duration-200 disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? t.form.submitting : t.form.submit}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
